<template>
  <div class="page-container">
    <div class="value-assessment-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner 
        :title-main1="va.title1"
        :title-main2="va.title2"
        :subtitle="va.subtitle"
        :placeholder="va.searchPlaceholder"
        :button-text="va.searchButton"
        :keyword.sync="searchKeyword"
        search-object="valueAssessment"
        :background-image="va.assets ? va.assets.bannerBg : localeImg('kejirencai/head-bg@2x.png')"
        @search="handleSearch"
      />

  

      <!-- 筛选条件区 -->
      <div class="filter-section">
        <div class="filter-left">
          <!-- 成果类别（一级） -->
          <div class="filter-row">
            <div class="filter-label">{{ va.filters ? va.filters.typeLabel : '' }}</div>
            <div class="filter-options">
              <div
                v-for="item in categoryMainOptions"
                :key="item.value"
                :class="['filter-option', { active: activeCategoryMain === item.value }]"
                @click="handleCategoryMainChange(item.value)"
              >
                {{ item.label }}
              </div>
            </div>
          </div>

          <!-- 成果子类（二级） -->
          <div class="filter-row" v-if="visibleCategorySubOptions.length > 0">
            <div class="filter-label">{{ va.filters?.subCategoryLabel || '成果子类:' }}</div>
            <div class="filter-options">
              <div
                v-for="item in visibleCategorySubOptions"
                :key="item.value"
                :class="['filter-option', { active: activeCategorySub === item.value }]"
                @click="activeCategorySub = item.value; onFilterChange()"
              >
                {{ item.label }}
              </div>
            </div>
          </div>

          <!-- 所属领域 -->
          <div class="filter-row">
            <div class="filter-label">{{ va.filters?.fieldLabel || '' }}</div>
            <div class="filter-options">
              <div
                v-for="item in fieldOptions"
                :key="item.value"
                :class="['filter-option', { active: activeField === item.value }]"
                @click="activeField = item.value; onFilterChange()"
              >
                {{ item.label }}
              </div>
            </div>
          </div>

          <!-- 时间 -->
          <div class="filter-row">
            <div class="filter-label">{{ va.filters ? va.filters.timeLabel : '' }}</div>
            <div class="filter-options">
              <div
                v-for="item in timeOptions"
                :key="item.value"
                :class="['filter-option', { active: activeTime === item.value }]"
                @click="activeTime = item.value"
              >
                {{ item.label }}
              </div>
            </div>
          </div>

          <!-- 评分 -->
          <div class="filter-row">
            <div class="filter-label">{{ va.filters ? va.filters.scoreLabel : '' }}</div>
            <div class="filter-options">
              <div
                v-for="item in scoreOptions"
                :key="item.value"
                :class="['filter-option', { active: activeScore === item.value }]"
                @click="activeScore = item.value"
              >
                {{ item.label }}
              </div>
            </div>
          </div>

          <!-- 成熟度 -->
          <div class="filter-row">
            <div class="filter-label">{{ va.filters ? va.filters.maturityLabel : '' }}</div>
            <div class="filter-options">
              <div
                v-for="item in maturityOptions"
                :key="item.value"
                :class="['filter-option', { active: activeMaturity === item.value }]"
                @click="activeMaturity = item.value"
              >
                {{ item.label }}
              </div>
            </div>
          </div>
        </div>

        <div
          class="filter-action"
          :style="{ backgroundImage: evaluateAssets.bg ? `url(${evaluateAssets.bg})` : '' }"
        >
          <button
            type="button"
            class="evaluate-btn"
            :style="{ backgroundImage: evaluateAssets.buttonBg ? `url(${evaluateAssets.buttonBg})` : '' }"
            @click="handleEvaluateClick"
          >
            <img
              v-if="evaluateAssets.icon"
              :src="evaluateAssets.icon"
              alt=""
              class="btn-icon"
            />
            <span class="btn-text">{{ va.evaluateButton || '我要评估' }}</span>
          </button>
        </div>
      </div>

      <!-- 评估列表 -->
      <div class="assessment-list-container">
        <div v-if="loading" class="loading">{{ loadingText }}</div>
        <div v-else-if="!assessmentList.length" class="empty-tip">{{ emptyText }}</div>
        <div v-else class="assessment-grid">
          <div 
            v-for="item in assessmentList" 
            :key="item.id"
            class="assessment-card"
            @click="handleItemClick(item)"
          >
            <div class="card-header">
              <h4 class="card-title">{{ item.title }}</h4>
              <div class="card-meta">
                <span v-if="item.type" class="meta-tag meta-type">{{ item.type }}</span>
                <span v-if="item.tech" class="meta-tag meta-tech">{{ item.tech }}</span>
                <span v-if="item.date" class="meta-tag meta-date">{{ item.date }}</span>
              </div>
              <p class="card-desc">{{ item.description }}</p>
            </div>
            <div class="card-footer">
              <div v-if="item.price" class="footer-left">
                <span class="price-label">{{ va.card ? va.card.priceLabel : '' }}</span>
                <span class="price-value">{{ item.price }}</span>
              </div>
              <div class="footer-right">
                <div class="score-box">
                  <span class="score-value">{{ item.score }}</span>
                </div>
                <button class="view-btn">
                  {{ va.card ? va.card.viewDetail : '' }}
                  <i class="arrow-icon">→</i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="!loading" class="pagination">
          <span class="pagination-info">{{ pageInfoText }}</span>
          <div class="pagination-controls">
            <button class="page-btn" :disabled="currentPage === 1" @click="goFirstPage">{{ (va.pagination && va.pagination.firstPage) || '' }}</button>
            <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">{{ (va.pagination && va.pagination.prevPage) || '' }}</button>
            <button
              v-for="page in visiblePages"
              :key="page"
              :class="['page-number', { active: currentPage === page }]"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="nextPage">{{ (va.pagination && va.pagination.nextPage) || '' }}</button>
            <button class="page-btn" :disabled="currentPage === totalPages" @click="goLastPage">{{ (va.pagination && va.pagination.lastPage) || '' }}</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getHomeConfig } from '@/config/homeConfig'
import { getValueAssessmentConfig } from '@/config/valueAssessmentConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import { getText, getCurrentLocale } from '@/utils/i18nText'
import request from '@/utils/request'
import valueAssessmentApi from '@/api/valueAssessment'
import supplyDemandApi from '@/api/supplyDemand'
import { CATEGORY_ALL_LABEL, buildSubCategoryTabsFromDict, getVisibleSubCategoryTabs } from '@/utils/filterTabs'

const VA_FILTER_DICT_CODES = {
  type: 'yAchievementTypev2',
  field: 'yField',
  time: 'ylValuationPeriod',
  score: 'ylValuationScore',
  maturity: 'ylTechnologyReadinessLevel'
}

const PORTAL_HOME_URL = 'https://www.ylnkzh.cn/main/?menuId=9b1e12bd437844b7b94323aa2f17d1fb#/achievement-web/declarant/my-achievement'

export default {
  name: 'ValueAssessment',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner
  },
  data() {
    return {
      config: {},
      assessmentConfig: {},
      searchKeyword: '',
      activeCategoryMain: 'all',
      activeCategorySub: '',
      activeField: 'all',
      activeTime: '',
      activeScore: '',
      activeMaturity: '',
      currentPage: 1,
      pageSize: 12,
      totalCount: 0,
      loading: false,

      // 面包屑数据
      breadcrumbItems: [],

      // 筛选选项
      categoryDictData: [],
      categoryMainOptions: [],
      categorySubOptions: [],
      fieldOptions: [],
      timeOptions: [],
      scoreOptions: [],
      maturityOptions: [],

      // 评估列表（接口 /open/value-assess-archive/list）
      assessmentList: []
    }
  },
  watch: {
    currentPage() {
      this.loadAssessmentList()
    },
    activeTime() {
      this.onFilterChange()
    },
    activeScore() {
      this.onFilterChange()
    },
    activeMaturity() {
      this.onFilterChange()
    }
  },
  computed: {
    va() {
      return this.assessmentConfig.valueAssessment || {}
    },

    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },

    // 页码最多显示 6 个，采用滑动窗口
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (!total || total <= max) {
        return Array.from({ length: total || 1 }, (_, i) => i + 1)
      }
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },

    pageInfoText() {
      const tpl = (this.va.pagination && this.va.pagination.pageInfo) || ''
      if (!tpl) return ''
      return tpl.replace('{total}', this.totalCount).replace('{pageSize}', this.pageSize)
    },

    loadingText() {
      return getText('common.loading')
    },

    emptyText() {
      return getText('common.noData') || '暂无数据'
    },

    evaluateAssets() {
      const assets = this.va.assets || {}
      return {
        bg: assets.filterActionBg || localeImg('valueAssessment/bg.png'),
        buttonBg: assets.evaluateBtnBg || localeImg('valueAssessment/button_bg.png'),
        icon: assets.evaluateBtnIcon || localeImg('valueAssessment/icon.png')
      }
    },

    visibleCategorySubOptions() {
      return getVisibleSubCategoryTabs(this.categorySubOptions)
    }
  },
  created() {
    this.loadConfig()
    this.loadFilterDictData()
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    // 设置缩放比例
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$el.querySelector('.value-assessment-page')
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    
    // 加载配置
    loadConfig() {
      this.config = getHomeConfig(getCurrentLocale())
      this.assessmentConfig = getValueAssessmentConfig(getCurrentLocale())

      this.breadcrumbItems = [
        { label: this.assessmentConfig.valueAssessment.breadcrumb?.home, path: '/index' },
        { label: this.assessmentConfig.valueAssessment.breadcrumb?.current, path: '' }
      ]
    },

    parseDictRows(result) {
      if (!result || result.status !== 0) return []
      if (result.data && Array.isArray(result.data.rows)) return result.data.rows
      if (Array.isArray(result.data)) return result.data
      if (typeof result.data === 'object' && result.data !== null) {
        return Object.values(result.data).flat()
      }
      return []
    },

    buildFlatFilterOptions(rows, dictCode) {
      const items = (rows || [])
        .filter(item => item && item.dictCode === dictCode)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

      return [
        { label: CATEGORY_ALL_LABEL, value: '' },
        ...items.map(item => ({
          label: item.itemValue || item.itemCode || '',
          value: String(item.itemCode || '')
        }))
      ]
    },

    buildFieldOptions(rows) {
      const fieldDict = (rows || [])
        .filter(item => item && item.dictCode === VA_FILTER_DICT_CODES.field)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

      return [
        { label: CATEGORY_ALL_LABEL, value: 'all' },
        ...fieldDict.map(item => ({
          label: item.itemValue || item.itemCode || '',
          value: String(item.itemCode || '')
        }))
      ]
    },

    buildCategoryOptions(dictData) {
      const categoryDict = (dictData || []).filter(item => item && item.dictCode === VA_FILTER_DICT_CODES.type)
      this.categoryDictData = categoryDict

      const mainTypes = categoryDict
        .filter(item => item.parentCode === null || item.parentCode === '' || item.parentCode === undefined)
        .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))

      this.categoryMainOptions = [
        { label: CATEGORY_ALL_LABEL, value: 'all' },
        ...mainTypes.map(item => ({
          label: item.itemValue || '',
          value: item.itemCode || ''
        }))
      ]

      this.updateCategorySubOptions(this.activeCategoryMain)
    },

    updateCategorySubOptions(mainValue) {
      if (!mainValue || mainValue === 'all') {
        this.categorySubOptions = []
        this.activeCategorySub = ''
        return
      }

      const subTypes = this.categoryDictData.filter(
        item => String(item.parentCode || '') === String(mainValue)
      )
      this.categorySubOptions = buildSubCategoryTabsFromDict(subTypes, {
        allLabel: CATEGORY_ALL_LABEL,
        allValue: ''
      })
      this.activeCategorySub = this.categorySubOptions[0]?.value ?? ''
    },

    handleCategoryMainChange(value) {
      this.activeCategoryMain = value
      this.updateCategorySubOptions(value)
      this.onFilterChange()
    },

    getCategoryQueryParams() {
      const params = {}
      if (this.activeCategoryMain && this.activeCategoryMain !== 'all') {
        params.firstCategory = this.activeCategoryMain
      }
      if (this.activeCategorySub) {
        params.secondCategory = this.activeCategorySub
      }
      return params
    },

    async loadFilterDictData() {
      try {
        const response = await supplyDemandApi.getDictData(Object.values(VA_FILTER_DICT_CODES))
        const rows = this.parseDictRows(response.data || {})

        this.buildCategoryOptions(rows)
        this.fieldOptions = this.buildFieldOptions(rows)
        this.timeOptions = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.time)
        this.scoreOptions = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.score)
        this.maturityOptions = this.buildFlatFilterOptions(rows, VA_FILTER_DICT_CODES.maturity)
      } catch (error) {
        console.error('加载价值评估筛选字典失败:', error)
        this.categoryMainOptions = [{ label: CATEGORY_ALL_LABEL, value: 'all' }]
        this.categorySubOptions = []
        this.fieldOptions = [{ label: CATEGORY_ALL_LABEL, value: 'all' }]
        this.timeOptions = [{ label: CATEGORY_ALL_LABEL, value: '' }]
        this.scoreOptions = [{ label: CATEGORY_ALL_LABEL, value: '' }]
        this.maturityOptions = [{ label: CATEGORY_ALL_LABEL, value: '' }]
      }

      this.activeCategoryMain = this.categoryMainOptions[0]?.value ?? 'all'
      this.activeCategorySub = ''
      this.activeField = this.fieldOptions[0]?.value ?? 'all'
      this.activeTime = this.timeOptions[0]?.value ?? ''
      this.activeScore = this.scoreOptions[0]?.value ?? ''
      this.activeMaturity = this.maturityOptions[0]?.value ?? ''

      this.loadAssessmentList()
    },

    onFilterChange() {
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        this.loadAssessmentList()
      }
    },

    getFilterQueryParams() {
      const params = {
        ...this.getCategoryQueryParams()
      }
      if (this.activeField && this.activeField !== 'all') {
        params.field = this.activeField
      }
      if (this.activeTime) params.valuationPeriod = this.activeTime
      if (this.activeScore) params.valuationScore = this.activeScore
      if (this.activeMaturity) params.technologyReadinessLevel = this.activeMaturity
      return params
    },

    formatArchiveTime(archiveTime) {
      if (!archiveTime) return ''
      const d = new Date(archiveTime)
      if (Number.isNaN(d.getTime())) return String(archiveTime).slice(0, 10)
      return `${d.getFullYear()}年${d.getMonth() + 1}月`
    },

    mapArchiveItem(item) {
      return {
        id: item.id,
        assessmentId: item.assessmentId,
        achievementId: item.achievementId,
        templateRecordId: item.templateRecordId,
        title: item.achievementName || '',
        description: item.techFeatures || '',
        date: this.formatArchiveTime(item.archiveTime),
        score: item.score != null && item.score !== '' ? String(item.score) : '',
        type: item.categoryLabel || item.achievementTypeLabel || '',
        tech: item.fieldLabel || '',
        price: ''
      }
    },

    async loadAssessmentList() {
      this.loading = true
      try {
        const response = await valueAssessmentApi.getArchiveList({
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          achievementName: (this.searchKeyword || '').trim(),
          ...this.getFilterQueryParams()
        })
        const result = response.data || {}
        const ok = result.code === 200 || result.status === 0
        if (ok && result.data) {
          const page = result.data
          const list = Array.isArray(page.list) ? page.list : []
          this.totalCount = Number(page.total) || 0
          const mapped = list.map(item => this.mapArchiveItem(item))
          this.assessmentList = mapped
        } else {
          this.assessmentList = []
          this.totalCount = 0
          console.error('价值评估列表加载失败:', result.msg || result.message)
        }
      } catch (error) {
        console.error('价值评估列表请求失败:', error)
        this.assessmentList = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1
      this.loadAssessmentList()
    },
    
    // 模块点击
    handleModuleClick(module) {
      console.log('点击模块:', module.name)
      // 路由跳转已在ModuleList组件中处理
    },
    
    async handleEvaluateClick() {
      try {
        const res = await request({
          url: '/uoc/umc/user/my/q/simpleinfo',
          method: 'get'
        })
        const result = res.data || {}
        if (result.status === 0 && result.data) {
          window.location.href = PORTAL_HOME_URL
          return
        }
      } catch (error) {
        console.error('检查登录状态失败:', error)
      }

      const currentUrl = encodeURIComponent(window.location.href)
      window.open(`/main/#/login?redirectURL=${currentUrl}`, '_blank')
    },

    // 点击评估卡片
    handleItemClick(item) {
      this.$router.push({
        path: '/value-assessment/detail',
        query: {
          id: item.id,
          templateRecordId: item.templateRecordId,
          assessmentId: item.assessmentId,
          achievementId: item.achievementId,
          title: item.title
        }
      })
    },
    
    // 上一页
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    
    // 下一页
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },

    goFirstPage() {
      if (this.currentPage === 1) return
      this.currentPage = 1
    },

    goLastPage() {
      if (this.currentPage === this.totalPages) return
      this.currentPage = this.totalPages
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.value-assessment-page {
  width: 1920px;
  min-height: 100vh;
  background: #ffffff;
  transform-origin: top left;
}

/* 成果智能评估区域 */
.intelligent-assessment-section {
  width: 1700px;
  margin: 20px auto;
  margin-left: 110px;
}

.assessment-banner {
  background: linear-gradient(90deg, #5B9FD8 0%, #7AB8E8 100%);
  border-radius: 8px;
  padding: 30px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 12px rgba(91, 159, 216, 0.2);
  
  .banner-left {
    flex: 1;
    
    .banner-title {
      font-family: Source Han Sans CN;
      font-size: 28px;
      font-weight: 600;
      color: #FFFFFF;
      margin: 0 0 12px 0;
    }
    
    .banner-subtitle {
      font-family: Source Han Sans CN;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);
      margin: 0;
    }
  }
  
  .banner-right {
    width: 200px;
    height: 120px;
    
    .banner-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
}

/* 筛选条件区 */
.filter-section {
  width: 1700px;
  margin: 20px auto;
  margin-left: 110px;
  background: #FFFFFF;
  border-radius: 4px;
  padding: 20px 30px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: stretch;
  gap: 24px;
}

.filter-left {
  flex: 1;
  min-width: 0;
}

.filter-action {
  position: relative;
  width: 260px;
  flex-shrink: 0;
  align-self: stretch;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: contain;

  .evaluate-btn {
    position: absolute;
    left: 50%;
    bottom: 18%;
    transform: translateX(-50%);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 208px;
    height: 48px;
    padding: 0 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    background-repeat: no-repeat;
    background-position: center;
    background-size: 100% 100%;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.9;
    }

    .btn-icon {
      width: 22px;
      height: 22px;
      object-fit: contain;
      flex-shrink: 0;
    }

    .btn-text {
      font-family: Source Han Sans CN;
      font-size: 18px;
      font-weight: 600;
      color: #FFFFFF;
      white-space: nowrap;
      line-height: 1;
    }
  }
}

.filter-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .filter-label {
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #666666;
    min-width: 80px;
    flex-shrink: 0;
    padding-top: 8px;
  }
  
  .filter-options {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }
  
  .filter-option {
    padding: 6px 16px;
    background: #F8F8F8;
    border: 1px solid transparent;
    border-radius: 4px;
    font-family: Source Han Sans CN;
    font-size: 14px;
    color: #666666;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background: #FFF9E6;
      border-color: rgba(160, 144, 60, 0.3);
      color: #A0903C;
    }
    
    &.active {
      background: #A0903C;
      border-color: #A0903C;
      color: #FFFFFF;
      font-weight: 500;
    }
  }
}

/* 评估列表容器 */
.assessment-list-container {
  width: 1700px;
  margin: 0 auto 40px;
  margin-left: 110px;

  .loading,
  .empty-tip {
    text-align: center;
    padding: 40px;
    font-size: 14px;
    color: #666666;
  }
}

.assessment-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.assessment-card {
  background: #FFFFFF;
  border: 1px solid #E8E8E8;
  border-radius: 4px;
  padding: 0;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-3px);
    border-color: #A0903C;
  }
  
  .card-header {
    padding: 20px;
    flex: 1;
    
    .card-title {
      font-family: Source Han Sans CN;
      font-size: 16px;
      font-weight: 600;
      color: #333333;
      margin: 0 0 12px 0;
      line-height: 1.5;
    }
    
    .card-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      
      .meta-tag {
        padding: 2px 8px;
        border-radius: 2px;
        font-size: 12px;
        line-height: 1.4;
        
        &.meta-type {
          background: #E8F4FF;
          color: #3B8BDB;
        }
        
        &.meta-tech {
          background: #FFF3E8;
          color: #D97706;
        }
        
        &.meta-date {
          background: #F0F0F0;
          color: #666666;
        }
      }
    }
    
    .card-desc {
      font-family: Source Han Sans CN;
      font-size: 12px;
      color: #777777;
      line-height: 1.6;
      margin: 0;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .card-footer {
    padding: 15px 20px;
    margin-top: auto;
    background: #F8F8F8;
    border-top: 1px solid #E8E8E8;
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    .footer-left {
      display: flex;
      align-items: baseline;
      gap: 6px;
      
      .price-label {
        font-family: Source Han Sans CN;
        font-size: 12px;
        color: #999999;
      }
      
      .price-value {
        font-family: Source Han Sans CN;
        font-size: 14px;
        font-weight: 500;
        color: #A0903C;
      }
    }
    
    .footer-right {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .score-box {
        width: 50px;
        height: 50px;
        background: #A0903C;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        
        .score-value {
          font-family: Source Han Sans CN;
          font-size: 24px;
          font-weight: 700;
          color: #FFFFFF;
        }
      }
      
      .view-btn {
        padding: 6px 16px;
        background: #A0903C;
        border: none;
        border-radius: 4px;
        font-family: Source Han Sans CN;
        font-size: 13px;
        color: #FFFFFF;
        cursor: pointer;
        transition: all 0.3s;
        display: flex;
        align-items: center;
        gap: 4px;
        
        &:hover {
          background: #A0903C;
        }
        
        .arrow-icon {
          font-style: normal;
        }
      }
    }
  }
}

/* 分页 */
.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  margin-top: 30px;

  .pagination-info {
    font-family: Source Han Sans CN;
    font-size: 13px;
    color: #666666;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .page-btn,
    .page-number {
      min-width: 36px;
      height: 32px;
      padding: 0 12px;
      background: #ffffff;
      border: 1px solid #dddddd;
      border-radius: 2px;
      font-family: Source Han Sans CN;
      font-size: 13px;
      color: #666666;
      cursor: pointer;
      transition: all 0.3s;

      &:hover:not(:disabled):not(.active) {
        border-color: #a0903c;
        color: #a0903c;
      }

      &.active {
        background: #a0903c;
        border-color: #a0903c;
        color: #ffffff;
      }

      &:disabled {
        background: #f5f5f5;
        border-color: #dddddd;
        color: #cccccc;
        cursor: not-allowed;
      }
    }
  }
}



@import '@/assets/scss/mixins/portal-filter-locale-zh-cn.scss';

.value-assessment-page.locale-zh-CN {
  @include portal-filter-locale-zh-cn;
}
</style>
